import { useAuth, useUser } from '../../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuAction } from '@ordr-as/umami-react/context-menu';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { Item } from '@ordr-as/umami-react/form';
import { HamburgerMenu, Header, TabMenu } from '@ordr-as/umami-react/header';
import FormattedMessage from '../FormattedMessage';
import { useQueryDigitalBillFeatureFlag, useQuickerLoginEnabled } from '../../hooks/useFeatureFlagApi';
import LocationPicker from './LocationPicker';
import { useIosAppVersion } from '../../hooks/useIosAppVersion';
import { useF1AppVersion } from '../../hooks/useF1AppVersion';
import { useQueryLocation } from '../../hooks/useLocationApi';
import ChangeUser from './ChangeUser';
import LogOut from './LogOut';
import TermsAndConditions from './TermsAndConditions';
import AdminRedirect from './AdminRedirect';
import PinScreenSettings from './PinScreenSettings';
import { createTerminalModeUrl } from '../../utils/linkBuilder';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useTerminalModeEnabled } from '../../hooks/useTerminalModeEnabled';
import AppReleaseVersion from './AppReleaseVersion';
import { isF1Device } from '../../utils/isF1Device';

declare global {
  interface Window {
    Android?: {
      navigateToTerminal?: () => void;
      logout?: () => void;
      changeLocation?: () => void;
      getConnectionState?: () => boolean;
    };
  }
}

const nonLocationRoutes = ['locations', 'softpay']; // dette må vi få til bedre

const AppHeader = () => {
  const location = useLocation();
  const { data: iosAppVersion } = useIosAppVersion();
  const { data: f1AppVersion } = useF1AppVersion();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { logout, changeUser } = useAuth();
  const { email } = useUser() || {};

  const [showSwitchToTerminalModeDialog, setShowSwitchToTerminalModeDialog] = useState<boolean>(false);

  const currentLocationId = location.pathname.split('/')[1]!;
  const isNotALocation = nonLocationRoutes.includes(currentLocationId);
  const locationId = isNotALocation ? undefined : currentLocationId;

  const { data: favritLocation } = useQueryLocation(locationId);

  const { data: digitalBillFeatureFlag } = useQueryDigitalBillFeatureFlag(favritLocation?.id);

  const { data: quickerLoginEnabled } = useQuickerLoginEnabled(favritLocation?.id);

  const isTerminalModeEnabled = useTerminalModeEnabled();
  const isF1 = isF1Device();
  const canSignalAndroidTerminal = !!window.Android?.navigateToTerminal;
  const canChangeLocation = !!window.Android?.changeLocation;

  const appVersion = iosAppVersion ?? f1AppVersion;
  const showLogoutButton = !(appVersion && !quickerLoginEnabled?.enabled);

  const selectedTab = location.pathname.split('/')[2] === 'open-bill' ? 'open-bill' : 'menu';

  const currentLocation = window.location.href;

  const handleTerminalModeClick = () => {
    setShowSwitchToTerminalModeDialog(true);
  };

  const handleTerminalModeConfirm = (shouldNavigate: boolean) => {
    setShowSwitchToTerminalModeDialog(false);

    if (shouldNavigate) {
      navigate(createTerminalModeUrl(currentLocationId));
    }
  };

  if (currentLocation.includes('/cart')) {
    return null;
  }

  return (
    <>
      <Header headerClass="flex justify-between" hideBackButton={true} backAction={() => {}}>
        {digitalBillFeatureFlag?.waiterCanInitiate ? (
          <TabMenu selectedKeys={[selectedTab]} selectedBorderColor="border-brand-secondary" type="compact">
            <Item key={'menu'} textValue="menu">
              <Link to={`/${currentLocationId}`} className="text-brand-secondary">
                <FormattedMessage id="generic.menu" />
              </Link>
            </Item>

            <Item key={'open-bill'} textValue="open-bill">
              <Link
                to={`/${currentLocationId}/open-bill`}
                data-cy="open-bill"
                className="whitespace-nowrap text-brand-secondary"
              >
                <FormattedMessage id="generic.OpenBills" />
              </Link>
            </Item>
          </TabMenu>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-1 ml-2">
          <div className="flex flex-col text-right text-xs max-w-[33vw]">
            {favritLocation?.name && (
              <span className="text-brand-secondary font-semibold truncate">{favritLocation?.name}</span>
            )}
            <span className="text-grey-dark truncate">{email}</span>
          </div>

          <HamburgerMenu>
            {quickerLoginEnabled?.enabled && favritLocation && (
              <ChangeUser email={email} onClick={() => changeUser(favritLocation.id)} />
            )}
            {!iosAppVersion && !isF1 && !canChangeLocation && <LocationPicker />}
            {isF1 && canChangeLocation && (
              <MenuAction className="border-b border-grey-lightest" onClick={() => window.Android?.changeLocation?.()}>
                <span className="text-standard !text-brand-black">
                  {formatMessage({ id: 'header.changeLocation' })}
                </span>
              </MenuAction>
            )}
            {favritLocation && (
              <Link
                className="umami-context-menu-item border-b border-grey-lightest !py-3 !text-brand-black"
                to={`/${currentLocationId}/settings`}
                state={{ backgroundLocation: location }}
              >
                <FormattedMessage id="header.paymentMethods" />
              </Link>
            )}
            {favritLocation && (
              <Link
                className="umami-context-menu-item border-b border-grey-lightest !py-3 !text-brand-black"
                to={`/${currentLocationId}/completed-payments`}
                state={{ backgroundLocation: location }}
              >
                <FormattedMessage id="header.payments" />
              </Link>
            )}
            {quickerLoginEnabled?.enabled && favritLocation && <PinScreenSettings />}
            {isTerminalModeEnabled && (
              <MenuAction className="border-b border-grey-lightest" onClick={handleTerminalModeClick}>
                <span className="text-standard !text-brand-black">{formatMessage({ id: 'header.terminalMode' })}</span>
              </MenuAction>
            )}
            {canSignalAndroidTerminal && (
              <MenuAction
                className="border-b border-grey-lightest"
                onClick={() => window.Android?.navigateToTerminal?.()}
              >
                <span className="text-standard !text-brand-black">Open terminal mode</span>
              </MenuAction>
            )}
            {!iosAppVersion && !isF1 && <AdminRedirect locationId={favritLocation?.id} />}
            <TermsAndConditions />
            {showLogoutButton && (
              <LogOut
                onClick={() => {
                  if (isF1) {
                    window.Android?.logout?.();
                    return;
                  }
                  logout();
                }}
              />
            )}
            <AppReleaseVersion isF1={canSignalAndroidTerminal} />
          </HamburgerMenu>
        </div>
      </Header>

      {showSwitchToTerminalModeDialog && (
        <Dialog
          title="Go to terminal mode?"
          onClose={() => handleTerminalModeConfirm(false)}
          onAction={() => handleTerminalModeConfirm(true)}
          actionLabel="Go to terminal mode"
        >
          <p>The terminal will display a static screen sutiable for facing the guests.</p>
          <p className="mt-4">You can switch back to ePOS mode in the hamburger menu on the terminal anytime.</p>
        </Dialog>
      )}
    </>
  );
};
export default AppHeader;
